<!-- frontend/src/components/charts/LeadsStatusChart.vue -->
<template>
  <div class="relative">
    <!-- Загрузка -->
    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
      <div class="text-center">
        <div class="loading-spinner mx-auto mb-2"></div>
        <p class="text-sm text-gray-500">Загрузка данных...</p>
      </div>
    </div>

    <!-- Ошибка -->
    <div v-else-if="error" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ExclamationTriangleIcon class="h-8 w-8 mx-auto text-red-400 mb-2" />
        <p class="text-gray-600">Ошибка загрузки диаграммы</p>
        <p class="text-sm text-gray-500 mt-1">{{ error }}</p>
        <button @click="loadData" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
          Попробовать снова
        </button>
      </div>
    </div>

    <!-- Пустые данные -->
    <div v-else-if="!chartData || chartData.length === 0" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ChartPieIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p class="text-gray-500">Нет данных для отображения</p>
      </div>
    </div>

    <!-- Диаграмма -->
    <div v-else class="h-64">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <!-- Сводная информация -->
    <div v-if="chartData && chartData.length > 0" class="mt-4 pt-4 border-t border-gray-200">
      <div class="flex items-center justify-between text-sm text-gray-600">
        <span>Всего статусов: {{ chartData.length }}</span>
        <span>Общее количество: {{ totalCount.toLocaleString('ru-RU') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, readonly } from 'vue'
import { ExclamationTriangleIcon, ChartPieIcon } from '@heroicons/vue/24/outline'
import { useChart, createPieChartConfig, validateChartData } from '@/composables/useChart'
import { useDataStore } from '@/stores/data'
import { transformLeadStatusData, getLeadStatusName } from '@/utils/bitrixStatusMapping'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  autoLoad: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['chartClick', 'dataLoaded', 'error', 'refresh'])

const dataStore = useDataStore()

// Состояние
const isLoading = ref(false)
const error = ref(null)
const chartData = ref([])
const chartCanvas = ref(null)

// Конфигурация диаграммы с проверкой данных
const chartConfig = computed(() => {
  console.log('LeadsStatusChart: computed chartConfig вызван, chartData.value:', chartData.value)

  if (!chartData.value || chartData.value.length === 0) {
    console.log('LeadsStatusChart: Нет данных для создания конфигурации')
    return null
  }

  // Валидация данных перед созданием конфигурации
  const validation = validateChartData(chartData.value, ['_id', 'count'])

  if (!validation.isValid) {
    console.warn('LeadsStatusChart: Невалидные данные для диаграммы:', validation.error)
    error.value = validation.error
    return null
  }

  try {
    const config = createPieChartConfig(validation.data, '_id', 'count', {
      showLegend: true,
      legendPosition: 'right',
      showPercentages: true,
      responsive: true,
      cutout: 0
    })

    if (!config) {
      console.error('LeadsStatusChart: createPieChartConfig вернул null')
      error.value = 'Не удалось создать конфигурацию диаграммы'
      return null
    }

    console.log('LeadsStatusChart: Конфигурация диаграммы создана успешно')
    return config
  } catch (err) {
    console.error('LeadsStatusChart: Ошибка создания конфигурации диаграммы:', err)
    error.value = 'Ошибка создания диаграммы'
    return null
  }
})

// КРИТИЧЕСКИ ВАЖНО: Полная изоляция от Vue реактивности
const chartConfigRaw = computed(() => {
  if (!chartConfig.value) {
    console.log('LeadsStatusChart: chartConfig.value пустой')
    return null
  }

  try {
    console.log('LeadsStatusChart: Создаем полностью изолированную конфигурацию')

    // КРИТИЧЕСКИ ВАЖНО: Создаем полностью новый объект без Vue реактивности
    const rawConfig = {
      type: chartConfig.value.type,
      data: {
        labels: chartConfig.value.data?.labels ? [...chartConfig.value.data.labels] : [],
        datasets: chartConfig.value.data?.datasets ? chartConfig.value.data.datasets.map(dataset => ({
          label: dataset.label,
          data: dataset.data ? [...dataset.data] : [],
          backgroundColor: dataset.backgroundColor ? [...dataset.backgroundColor] : [],
          borderColor: dataset.borderColor,
          borderWidth: dataset.borderWidth,
          hoverBorderWidth: dataset.hoverBorderWidth,
          hoverOffset: dataset.hoverOffset
        })) : []
      },
      options: chartConfig.value.options ? {
        responsive: chartConfig.value.options.responsive,
        maintainAspectRatio: chartConfig.value.options.maintainAspectRatio,
        cutout: chartConfig.value.options.cutout,
        plugins: chartConfig.value.options.plugins ? {
          legend: chartConfig.value.options.plugins.legend ? {
            display: chartConfig.value.options.plugins.legend.display,
            position: chartConfig.value.options.plugins.legend.position,
            labels: chartConfig.value.options.plugins.legend.labels ? {
              padding: 15,
              usePointStyle: true,
              font: { size: 12 },
              generateLabels: chartConfig.value.options.plugins.legend.labels.generateLabels
            } : { padding: 15, usePointStyle: true, font: { size: 12 } }
          } : {},
          tooltip: chartConfig.value.options.plugins.tooltip ? {
            callbacks: chartConfig.value.options.plugins.tooltip.callbacks || {}
          } : {}
        } : {},
        animation: chartConfig.value.options.animation ? {
          animateRotate: chartConfig.value.options.animation.animateRotate,
          animateScale: chartConfig.value.options.animation.animateScale,
          duration: chartConfig.value.options.animation.duration
        } : {},
        interaction: chartConfig.value.options.interaction ? {
          intersect: chartConfig.value.options.interaction.intersect
        } : {}
      } : {}
    }

    console.log('LeadsStatusChart: Изолированная конфигурация создана:', rawConfig.type)
    return rawConfig

  } catch (err) {
    console.error('LeadsStatusChart: Ошибка создания изолированной конфигурации:', err)
    return null
  }
})

// Используем chartConfigRaw вместо chartConfig
const { updateChart, createChart } = useChart(chartCanvas, chartConfigRaw, {
  onChartReady: (chart) => {
    console.log('LeadsStatusChart: onChartReady, chart instance:', chart)

    // НЕ ИЗМЕНЯЕМ конфигурацию после создания диаграммы!
    // Все настройки legend уже заданы в createPieChartConfig

    // Добавляем только обработчик кликов
    chart.options.onClick = (event, elements) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index
        const dataItem = chartData.value[elementIndex]

        if (dataItem) {
          console.log('LeadsStatusChart: Клик по элементу:', dataItem)
          emit('chartClick', {
            data: dataItem,
            index: elementIndex
          })
        }
      }
    }

    console.log('LeadsStatusChart: onChartReady завершен успешно')
  }
})

// Вычисляемые свойства
const totalCount = computed(() => {
  if (!chartData.value || !Array.isArray(chartData.value)) {
    return 0
  }

  return chartData.value.reduce((sum, item) => {
    const count = item?.count
    return sum + (typeof count === 'number' ? count : 0)
  }, 0)
})

// Методы
const loadData = async () => {
  // Если переданы данные через props, используем их
  if (props.data && props.data.length > 0) {
    console.log('LeadsStatusChart: Используем данные из props')

    // Преобразуем коды статусов в читаемые названия
    const transformedData = transformLeadStatusData(props.data)
    chartData.value = transformedData
    emit('dataLoaded', transformedData)
    return
  }

  if (!props.autoLoad) {
    console.log('LeadsStatusChart: Автозагрузка отключена')
    return
  }

  try {
    isLoading.value = true
    error.value = null

    console.log('📊 LeadsStatusChart: Загрузка данных статусов лидов...')

    // Загружаем данные через dataStore
    const response = await dataStore.aggregateData({
      collection_name: 'leads',
      aggregation_type: 'count',
      group_by: 'STATUS_ID'
    })

    console.log('📊 LeadsStatusChart: Ответ от API:', response)

    // API возвращает объект с полем data, которое содержит массив
    if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
      console.log('📊 LeadsStatusChart: Исходные данные:', response.data)

      // Преобразуем коды статусов в читаемые названия
      const transformedData = transformLeadStatusData(response.data)
      console.log('📊 LeadsStatusChart: Преобразованные данные:', transformedData)

      chartData.value = transformedData
      emit('dataLoaded', transformedData)
      console.log('✅ LeadsStatusChart: Данные загружены:', transformedData.length, 'статусов')
    } else {
      console.log('⚠️ LeadsStatusChart: Получены пустые данные, генерируем тестовые')
      // Генерируем тестовые данные
      const testData = generateTestData()
      chartData.value = testData
      emit('dataLoaded', testData)
    }
  } catch (err) {
    console.error('❌ LeadsStatusChart: Ошибка загрузки данных:', err)
    error.value = err.message || 'Ошибка загрузки данных'
    emit('error', err)

    // В случае ошибки также показываем тестовые данные
    const testData = generateTestData()
    chartData.value = testData
    emit('dataLoaded', testData)
  } finally {
    isLoading.value = false
  }
}

const generateTestData = () => {
  const statuses = [
    { _id: 'NEW', name: 'Новый' },
    { _id: 'IN_PROCESS', name: 'В работе' },
    { _id: 'QUALIFIED', name: 'Квалифицирован' },
    { _id: 'CONVERTED', name: 'Конвертирован' },
    { _id: 'WON', name: 'Успешно' },
    { _id: 'JUNK', name: 'Некачественный' }
  ]

  return statuses.map(status => ({
    _id: getLeadStatusName(status._id), // Используем функцию маппинга
    originalCode: status._id, // Сохраняем оригинальный код
    count: Math.floor(Math.random() * 50) + 5
  }))
}

// Watchers
watch(() => props.data, (newData) => {
  if (newData && newData.length > 0) {
    console.log('LeadsStatusChart: Получены новые данные через props')

    // Преобразуем коды статусов в читаемые названия
    const transformedData = transformLeadStatusData(newData)
    chartData.value = transformedData
    emit('dataLoaded', transformedData)
  }
}, { immediate: true, deep: true })

// Инициализация
onMounted(() => {
  console.log('LeadsStatusChart: onMounted, chartCanvas:', chartCanvas.value)
  
  nextTick(() => {
    console.log('LeadsStatusChart: after nextTick, chartCanvas:', chartCanvas.value)
    loadData()
  })
})

// Очистка при размонтировании
onUnmounted(() => {
  console.log('🔄 LeadsStatusChart: Компонент размонтирован')
})

// Методы для родительского компонента
const refresh = () => {
  console.log('LeadsStatusChart: Ручное обновление данных')
  loadData()
}

// Экспортируем методы
defineExpose({
  refresh,
  chartData: readonly(chartData),
  isLoading: readonly(isLoading),
  error: readonly(error)
})
</script>

<style scoped>
.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #f3f4f6;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>